import { Component, EventEmitter, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { forkJoin } from 'rxjs';
import { DocumentReference, DocumentService } from 'src/app/documents/documents.service';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { PgMessagesService, MessageLevel } from 'src/app/shared/services/pg-messages.service';
import { CrewChangeServices, CredentialItemsMap } from 'src/app/transportation/vessels/crew-change/crew-change.service';
import { KeycloakService } from '../../../../authentication/keycloak.service';
import { saveAs } from 'file-saver';
import { SharedModule } from 'src/app/shared/shared.module';

import { MaterialImageViewerComponent } from 'src/app/shared/components/material-image-viewer/material-image-viewer';
import { MatInputModule, MatLabel } from '@angular/material/input';
import { MaterialOrganisationSelectorComponent } from 'src/app/shared/components/material-autocomplete/material-organisationselector.component';
import { MaterialPgInputComponent } from 'src/app/shared/components/material-pg-input/material-pg-input.component';
import { MaterialChunkFileUploadComponent } from 'src/app/shared/components/material-file-uploader/material-chunk-file-upload.component';
import { MaterialAutoCompleteComponent } from 'src/app/shared/components/material-autocomplete/material-auto-complete.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MaterialPdfViewerPopUpComponent } from 'src/app/shared/components/material-pdf-viewer-pop-up/material-pdf-viewer-pop-up.component';
import { Crew, CrewDocument, Remark } from 'src/app/generated/services/transportation/crew/crew';
import { CrewServiceSpa } from 'src/app/generated/services/transportation/crew/crewSpa';
import { AuthenticationModule } from 'src/app/authentication/authentication.module';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-crew',
  templateUrl: './crew.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SharedModule,
    RouterModule,
    MaterialPdfViewerPopUpComponent,
    MaterialImageViewerComponent,
    MatInputModule,
    MaterialOrganisationSelectorComponent,
    MaterialPgInputComponent,
    MaterialChunkFileUploadComponent,
    MaterialAutoCompleteComponent,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCardModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatLabel,
    AuthenticationModule],
})
export class CrewComponent implements OnInit {
  title = new EventEmitter<string>();
  crew!: Crew;
  expectedVersion!: number;
  message!: string;
  nationalityItem: Organisation[] = [];
  crewId!: string;
  nationality: Organisation = Organisation.create();
  minDateExpiryDate = moment().format('YYYY-MM-DD');
  maxDate = moment().format('YYYY-MM-DD');
  oldPassportDetails!: CrewDocument;
  oldSeamanDetails!: CrewDocument;
  oldContactNumber!: string;
  tempCredential = CrewDocument.create();
  tempRemark = Remark.create();
  credentials = Array.from(CredentialItemsMap.keys());
  showDocumentEntry!: boolean;
  documentEntryTitle!: string;
  _selectedCredential!: string | undefined;
  showRemark!: boolean;
  remarkMinDate!: string;
  remarkMaxDate!: string;
  doc?: DocumentReference;
  doc2?: DocumentReference;

  set selectedCredentialDocument(value: string) {
    this._selectedCredential = CredentialItemsMap.get(value);
    if (this._selectedCredential) {
      this.documentEntryTitle = value;
      const temp = (this.crew.credentials) ? this.crew.credentials.find(c => c.documentName === this._selectedCredential) : undefined;
      this.tempCredential = (temp) ? temp : {
        documentName: this._selectedCredential,
        issueDate: '',
        expirationDate: '',
        fileId: '',
        number: '',
      };
      if (this._selectedCredential === 'seamanBook' && this.crew.seamanDetails) {
        this.tempCredential = this.crew.seamanDetails;
      }
      if (this._selectedCredential === 'passport' && this.crew.passportDetails) {
        this.tempCredential = this.crew.passportDetails;
      }
      this.showDocumentEntry = true;
    } else {
      this.showDocumentEntry = false;
    }
  }

  showDocs!: boolean[];
  showRemarkDoc!: boolean[];
  showSeamanBook!: boolean[];
  showPassport!: boolean;
  showCV!: boolean;
  authors: Crew[] = [];
  author!: Crew;

  constructor(
    private _crewChangeService: CrewChangeServices,
    private _pgMessagesService: PgMessagesService,
    private _activateRoute: ActivatedRoute,
    private _organisationService: OrganisationService,
    private _keycloakService: KeycloakService,
    private _documentService: DocumentService,
    private _crewService: CrewServiceSpa,
    private _router: Router,
  ) { }

  ngOnInit(): void {
    this.nationalityItem = [];
    this.title.emit('Crew');
    this._activateRoute.paramMap.subscribe(p => {
      this.crewId = p.get('id') ?? '';
      this.crew = Crew.create();
      forkJoin([
        this._organisationService.get(),
        // this._crewChangeService.getAllSpecificRank('Master')
      ]).subscribe((result) => {
        this.nationalityItem = result[0].filter(x => x.properties['country'] === true);
        // this.authors = result[2];
        this.initCrew();
        this.setMinMaxDate();
      });
    });
  }

  disableEdit() {
    return !this._keycloakService.hasRole('vessels-crew-change-editor');
  }

  changeStatus() {
    this.showRemark = true;
  }

  initCrew() {
    if (this.crewId) {
      this._crewService.getCrewById({id: this.crewId}).then((response) => {
        this.crew = response.data!;
        this.expectedVersion = response.version;
        this.showDocs = (this.crew.credentials) ? this.crew.credentials.map(() => false) : [];
        this.showRemarkDoc = (this.crew.remarks) ? this.crew.remarks.map(() => false) : [];
        this.nationality = this.nationalityItem.find(z => z.name === this.crew.nationality) || Organisation.create();
        this.oldContactNumber = this.crew.contactNumber ?? '';
        this.crew.remarks = this.crew.remarks.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      });
    }
  }

  credentialItemFormatter(credentialItems: string) {
    return credentialItems;
  }

  getCredentialsLabel(value: string) {
    const title = CredentialItemsMap.get(value);
    return title ? title : '';
  }

  uploadCredentialFile(r: DocumentReference) {
    this.tempCredential.fileId = r.id;
  }

  uploadRemarkFile(r: DocumentReference) {
    this.tempRemark.docId = r.id;
  }

  uploadCvFile(v: DocumentReference) {
    this.crew.cvDocumentId = v.id;
  }

  changePassportNumber() {
    this.crew.passportDetails!.expirationDate = '';
    this.crew.passportDetails!.issueDate = '';
    this.crew.passportDetails!.fileId = '';
  }

  changeSeamanNumber() {
    this.crew.seamanDetails!.expirationDate = '';
    this.crew.seamanDetails!.fileId = '';
  }

  async addCrew(crew: Crew) {
    this._crewService.addCrew(crew)
      .then((response) => {
        this.message = `${this.crew.firstName} ${this.crew.lastName}`;
        this._pgMessagesService.publishMessage({
          message: this.message,
          topic: 'Added New Crew',
          level: MessageLevel.Info
        });
        this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this._router
            .navigate(['/vessels/crew-change/crew/', response?.data?.id])
            .then((e) => console.log(`Result of Navigation is ${e}`));
        });
      })
      .catch(() => {
        this.message = 'Error';
        this._pgMessagesService.publishMessage({
          message: this.message,
          topic: 'Crew Insertion Error',
          level: MessageLevel.Error
        });
      });
  }

  async save() {
    if (!this.crew.passportDetails || !this.crew.seamanDetails) {
      alert('Passport or Seaman Book not imported');
      return;
    }
    const fLowercase = this.crew.firstName.toLowerCase();
    const lLowercase = this.crew.lastName.toLowerCase();
    const passTrim = this.crew.passportDetails.number.replace(/\s/g, '');
    this.crew.firstName = fLowercase[0].toUpperCase() + fLowercase.slice(1);
    this.crew.lastName = lLowercase[0].toUpperCase() + lLowercase.slice(1);
    this.crew.passportDetails.number = passTrim;
    this.crew.seamanDetails.number = this.crew.seamanDetails.number.replace(/\s/g, '');
    this.crew.nationality = this.nationality!.name;
    if (this.crewId) {
      try {
        await this._crewService.updateCrew({ data: this.crew, expectedVersion: this.expectedVersion });
        this.message = `${this.crew.firstName} ${this.crew.lastName}`;
        this._pgMessagesService.publishMessage({
          message: this.message,
          topic: 'Updating Crew',
          level: MessageLevel.Info
        });
      } catch {
        this.message = 'Error';
        this._pgMessagesService.publishMessage({
          message: this.message,
          topic: 'Crew Update Error',
          level: MessageLevel.Error
        });
      }
    } else {
      try{
        const x = await this._crewService.getCrewByName(this.crew);
        if (confirm(`${x.data} persons with the name ${this.crew.firstName} ${this.crew.lastName},\n Passport Numbers
          //     ${x.data?.passportDetails?.number} and\n SeaMan Numbers ${x.data?.seamanDetails?.number} Already exists.\n\nAre you sure you want to add a new one?`)) {
          this.addCrew(this.crew);
        }
      }
      catch {
        this.addCrew(this.crew);
      }
    }
  }

  photoUploaded(docReference: DocumentReference) {
    this.crew.photoDocumentId = docReference.id;
  }

  downLoadDocument(id:string) {
    this._documentService.download(id).subscribe(f => {
      saveAs(f.body, `${id}.pdf`);
    });
  }

  addToCredentials() {
    this.tempCredential.documentName = this._selectedCredential ?? '';
    if (!this.crew.credentials) {
      this.crew.credentials = [];
    }
    if (!this.crew.oldDetails) {
      this.crew.oldDetails = {
        docs: [],
        contactInfo: [],
      };
    }
    if (this._selectedCredential === 'seamanBook') {
      if (this.crewId && this.crew.seamanDetails!.number !== this.tempCredential.number) {
        this.crew.oldDetails.docs.push(this.crew.seamanDetails!);
      }
      this.crew.seamanDetails = this.tempCredential;
      this.showDocumentEntry = false;
      return;
    }
    if (this._selectedCredential === 'passport') {
      if (this.crewId && this.crew.passportDetails!.number !== this.tempCredential.number) {
        this.crew.oldDetails.docs.push(this.crew.passportDetails!);
      }
      this.crew.passportDetails = this.tempCredential;
      this.showDocumentEntry = false;
      return;
    }
    const credential = this.crew.credentials.findIndex(c => c.documentName === this._selectedCredential);
    if (credential >= 0) {
      this.crew.credentials[credential] = this.tempCredential;
    } else {
      this.crew.credentials.push(this.tempCredential);
    }
    this.showDocumentEntry = false;
    this.showDocs = (this.crew.credentials) ? this.crew.credentials.map(() => false) : [];
  }

  addToRemarks() {
    if (this.author) {
      this.tempRemark.author = this.author.id;
    }
    if (!this.crew.remarks) {
      this.crew.remarks = [];
    }
    this.crew.remarks.push(this.tempRemark);
    this.save();
    this.showRemark = false;
    this.tempRemark = Remark.create();
  }

  getActivationStatus() {
    return this._crewChangeService.getCrewActivationStatus(this.crew);
  }

  getLatestRemark() {
    if (!this.crew.remarks || this.crew.remarks.length === 0) {
      return 'active';
    }
    return this._crewChangeService.getLatestStatus(this.crew);
  }

  setMinMaxDate() {
    this.remarkMaxDate = new Date().toISOString().split('T')[0];
    if (this.crew.remarks && this.crew.remarks.length > 0) {
      this.remarkMinDate = this.crew.remarks.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0].date;
    }
    this.remarkMinDate = '0001-01-01';
  }

  crewFormatter(crew: Crew) {
    if (crew) {
      return `${crew.firstName} ${crew.lastName}`;
    }
    return '';
  }

  findCrew(id: string) {
    if (id) {
      const master = this.authors.find(a => a.id === id);
      if (master) {
        return `${master.firstName} ${master.lastName}`;
      }
    }
    return '';
  }

  editRemark(remark: Remark) {
    this.tempRemark = remark;
    this.showRemark = true;
  }

  removeRemark(index: number) {
    this.crew.remarks.splice(index, 1);
    this.save();
  }
}
